export type Contract = {
  airtime: number
  shareOfViewership: number
  contractUrl?: string
  contractExpiry?: string
}

export type StreamerNote = {
  content: string
  createdAt?: string
  createdByName?: string
}

export type StreamerTag = {
  id: number
  name: string
  assignedStreamerCount?: number
  status?: "ACTIVE" | "DELETED"
  createdAt?: string
}

export type ChannelValidationStatus = "PENDING" | "VALID" | "INVALID"

export type StreamingPlatform = {
  platform: string
  url: string
  isPrimary: boolean
  validationStatus?: ChannelValidationStatus | null
  validationError?: string | null
  isActive?: boolean
}

export type SaMetadata = {
  externalStreamerId: number
  thumbnailUrl: string
  assignee: string | null
  aggregateFollowers: number
  languages: string[] | null
  channels: {
    platform: string
    channelName: string | null
    channelUrl: string
    channelImageUrl: string | null
  }[]
}

export const INTERNAL_ROLES = [
  "PLATFORM_ENGINEER",
  "SUPER_ADMIN",
  "ADMIN",
  "TEAM_LEAD",
  "RELATIONSHIP_MANAGER",
] as const

export const STREAMER_ROLE = "STREAMER" as const

export type InternalRole = (typeof INTERNAL_ROLES)[number]

export type ROLES = InternalRole | typeof STREAMER_ROLE

export function isInternalRole(
  accountType: string | null | undefined
): accountType is InternalRole {
  return (
    accountType != null &&
    (INTERNAL_ROLES as readonly string[]).includes(accountType)
  )
}

export function isAdminAccountType(
  accountType: string | null | undefined
): boolean {
  return (
    accountType === "ADMIN" ||
    accountType === "SUPER_ADMIN" ||
    accountType === "PLATFORM_ENGINEER"
  )
}

export function isSuperAdminAccountType(
  accountType: string | null | undefined
): boolean {
  return accountType === "SUPER_ADMIN" || accountType === "PLATFORM_ENGINEER"
}

export const ADD_STREAMER_ROLES = [
  "PLATFORM_ENGINEER",
  "SUPER_ADMIN",
  "ADMIN",
  "TEAM_LEAD",
] as const

export function canAddStreamer(
  accountType: string | null | undefined
): boolean {
  return (
    accountType != null &&
    (ADD_STREAMER_ROLES as readonly string[]).includes(accountType)
  )
}

export type RiskProfile = "GREEN" | "AMBER" | "RED" | "BLACK"

export type TrackingStatus = "MANAGED" | "TRACKED" | "BLACKLISTED"

export type PreferredGameType = "SLOTS" | "LIVE_CASINO"

export type StreamStatsSyncStatus =
  "NOT_SYNCED" | "SYNC_PENDING" | "SYNCED" | "SYNC_FAILED"

export type Profile = {
  id?: string
  name: string
  email: string
  accountType: ROLES
  residentialAddress: string
  language?: string | null
  segment?: string | null
  countryOfResidence?: string | null
  region?: string | null
  timezone?: string | null
  relationshipManager?: string | null
  /** Relationship manager display name from the streamer-account API. */
  managerName?: string | null
  streamingPlatforms: StreamingPlatform[]
  discordUsername: string
  marketingConsent: boolean
  contract: Contract
  streamingUsername?: string
  status?: "ACTIVE" | "DEACTIVATED" | "PENDING"
  createdAt?: string
  privacyPolicyAcceptedAt?: string
  lastLoginAt?: string
  airtime?: number
  shareOfAirtime?: number
  lastStreamed?: string
  streamStatsSyncStatus?: StreamStatsSyncStatus | null
  password?: string
  saMetadata?: SaMetadata
  notes?: StreamerNote[]
  tags?: StreamerTag[]
  tagIds?: number[]
  riskProfile?: RiskProfile | null
  trackingStatus?: TrackingStatus | null
  preferredGameType?: PreferredGameType | null
  /** Internal staff accounts only (partial-update contract: `location`). */
  location?: string | null
  /**
   * Manager id: reporting lead for internal accounts, or assigned relationship
   * manager for streamers. Number may appear on raw API payloads before parse.
   */
  managerId?: string | number | null
}

/** Feature keys returned by `/api/v1/auth/me` under `features`. */
export const USER_FEATURE_KEYS = {
  CHALLENGES_CLIPS: "CHALLENGES_CLIPS",
} as const

export type UserFeatureKey =
  (typeof USER_FEATURE_KEYS)[keyof typeof USER_FEATURE_KEYS]

export type AuthenticatedUserInfo = {
  id?: string
  email: string
  name: string
  accountType: ROLES
  requiresPasswordChange: boolean
  requiresPrivacyPolicyAcceptance: boolean
  /** Entitlements from `/me` (e.g. `CHALLENGES_CLIPS`). */
  features: string[] | null
}

export function hasUserFeature(
  features: readonly string[] | null | undefined,
  key: UserFeatureKey | string
): boolean {
  return Boolean(features?.includes(key))
}
